<template>
  <div class="flex flex-col">
    <div class="mb-4">
      <gd-button @click="$emit('clickedBackButton')" size="auto" class="p-4">
        {{ $t('frontend_domain_wizard_back_to_my_domains') }}</gd-button
      >
    </div>

    <div>
      <steps :steps="steps" :activeStep="activeStep"></steps>
    </div>

    <div class="flex flex-col items-center w-full h-128 mt-8">
      <div v-if="activeStep == 1" class="w-2/3 flex flex-col justify-center">
        <div
          class="
            w-full
            flex flex-col
            justify-center
            items-center
            content-center
          "
        >
          <p class="font-bold text-lg text-dark antialiased text-center mx-24">
            {{ $t('frontend_domain_wizard_what_domain_to_add') }}
          </p>

          <p class="text-grey-600 antialiased text-center mt-4">
            {{ $t('frontend_domain_wizard_select_type_domain_add_free_ssl_desc') }}
          </p>

          <div class="w-full flex flex-row mb-1 mt-4">
            <div
              v-for="(domainHostingType, index) in domainHostingTypes"
              :key="index"
              class="flex w-1/2 mr-4"
            >
              <gd-custom-radio
                v-model="newDomainType"
                :option="domainHostingType"
              ></gd-custom-radio>
            </div>
          </div>

          <div class="mt-2" v-if="newDomainType == 2">
            <span class="text-red">{{ $t('frontend_note') }}: </span> {{ $t('frontend_domain_wizard_ssl_subdomain_note') }}
            <a
              href="https://support.groovedigital.com/"
              target="_blank"
              class="underline"
              >{{ $t('frontend_gduikit_here') }}.</a
            >
          </div>
        </div>
        <div class="flex flex-col justify-center mt-2">
          <gd-button @click="saveFirstStep" size="auto" class="px-4" primary>
            {{ $t('frontend_next') }}
          </gd-button>
          <p class="mt-2 text-sm text-center text-red">{{ errorMsg }}</p>
        </div>
      </div>
      <div v-if="activeStep == 2" class="w-1/2 flex flex-col justify-center">
        <div
          class="
            w-full
            flex flex-col
            justify-center
            items-center
            content-center
          "
        >
          <p class="font-bold text-lg text-dark antialiased text-center mx-24">
            {{ $t('frontend_domain_wizard_which_domain_to_add') }}
          </p>

          <p class="text-grey-600 antialiased text-center mt-4">
            {{ $t('frontend_domain_wizard_url_domain_to_add_desc') }}
          </p>

          <div class="flex flex-row text-grey-600 mt-8">
            <div class="flex flex-wrap content-end mr-2">https://</div>
            <div class="flex flex-col mr-2">
              <text-input v-model="newDomainName"></text-input>
            </div>
          </div>
        </div>
        <div class="flex flex-row justify-center mt-2">
          <gd-button
            @click="activeStep = 1"
            size="auto"
            class="px-4 mr-2"
            primary
          >
            {{ $t('frontend_previous') }}
          </gd-button>
          <gd-button
            :loading="addingDomain"
            @click="saveSecondStep"
            size="auto"
            class="px-4"
            primary
          >
            {{ $t('frontend_next') }}
          </gd-button>
        </div>
        <p class="mt-2 text-sm text-center text-red">{{ errorMsg }}</p>
      </div>
      <div v-if="activeStep == 3" class="w-1/2 flex flex-col justify-center">
        <div
          class="
            w-full
            flex flex-col
            justify-center
            items-center
            content-center
          "
        >
          <p class="font-bold text-lg text-dark antialiased text-center mx-24">
            {{ $t('frontend_domain_wizard_connect_your_domain') }}
          </p>

          <p
            class="text-grey-600 antialiased text-center mt-4"
            v-if="newDomainType == 1"
          >
            {{ $t('frontend_domain_wizard_update_your_nameservers_desc') }}
          </p>
          <p class="text-grey-600 antialiased text-center mt-4" v-else>
            {{ $t('frontend_domain_wizard_create_a_txt_record_desc') }}
          </p>
          <div class="flex flex-col text-grey-600 mt-8 w-1/2">
            <div
              v-for="(dnsRecord, index) in selectedDomainRecords"
              :key="index"
            >
              <input-with-clipboard
                v-model="selectedDomainRecords[index]"
                class="mt-2"
              ></input-with-clipboard>
            </div>
          </div>
        </div>
        <div class="flex flex-col justify-center mt-2 w-full">
          <gd-button
            @click="activeStep = 4"
            size="auto"
            class="w-1/2 px-4 mx-auto"
            primary
          >
            {{ $t('frontend_next') }}
          </gd-button>
        </div>
      </div>
      <div v-if="activeStep == 4" class="w-1/2 flex flex-col justify-center">
        <div
          class="
            w-full
            flex flex-col
            justify-center
            items-center
            content-center
          "
        >
          <p class="text-grey-600 antialiased text-center mt-4">
            {{ $t('frontend_domain_wizard_verify_domain_config_desc') }}
          </p>
        </div>
        <div class="flex flex-col justify-center mt-2">
          <gd-button
            :loading="verifyingDomain"
            @click="validateDomainRecords"
            size="auto"
            class="px-4"
            primary
          >
            {{ $t('frontend_gduikit_validate_dns') }}
          </gd-button>
          <a
            @click="skipVerification"
            class="mt-2 text-center text-blue-dark cursor-pointer"
            >{{ $t('frontend_domain_wizard_validate_dns_later') }}</a
          >
        </div>
      </div>
      <div v-if="activeStep == 5" class="w-1/2 flex flex-col justify-center">
        <div
          class="
            w-full
            flex flex-col
            justify-center
            items-center
            content-center
          "
        >
          <p class="font-bold text-lg text-dark antialiased text-center mx-24">
            {{ $t('frontend_domain_wizard_domain_added_successfully') }}
          </p>

          <p class="text-grey-600 antialiased text-center mt-4">
            {{ $t('frontend_domain_wizard_your_domain_has_been_connected_desc') }}.
          </p>
        </div>
        <div class="flex flex-col justify-center mt-2">
          <gd-button
            @click="$emit('completedDomainSetup')"
            size="auto"
            class="px-4"
            primary
          >
            {{ $t('frontend_domain_wizard_complete_process') }}
          </gd-button>
        </div>
        <div v-if="newDomainType == 2" class="mt-2">
          <span class="text-red">{{ $t('frontend_note') }} : </span> {{ $t('frontend_domain_wizard_you_need_add_required_records_note') }}
        </div>
      </div>
    </div>

    <loading-overlay
      :is-loading="showLoader"
      :full-page="true"
    ></loading-overlay>
  </div>
</template>

<script>
import api from '@groovepages/gd-lib/api';
import Notify from '@groovepages/gd-lib/notify';
import { mapGetters } from 'vuex';
import Steps from './Steps';
import GdButton from './Button';
import TextInput from './TextInput';
import InputWithClipboard from './InputWithClipboard';
import domainStore from '../store';
import CopyBtn from './CopyBtn';
import ConfirmModal from './ConfirmModal';
import GdModal from './Modal';
import LoadingOverlay from './LoadingOverlay';
import GdCustomRadio from './GdCustomRadio';
import isValidDomain from '../modules/domain-validator/domain-validator';

export default {
  name: 'AddDomainWizard',
  components: {
    GdModal,
    GdCustomRadio,
    ConfirmModal,
    Steps,
    CopyBtn,
    GdButton,
    TextInput,
    InputWithClipboard,
    LoadingOverlay,
  },
  computed: {
    subDomain() {
      return (
        this.$store.getters.user.metadata &&
        this.$store.getters.user.metadata.subdomain
      );
    },
    freeUser() {
      return this.$store.getters.isLiteUser;
    },
    ...mapGetters({
      selectedDomain: 'domains/selectedDomain',
      selectedDomainRecords: 'domains/selectedDomainRecords',
    }),
  },
  watch: {
    newDomainName() {
    	this.newDomainName = this.newDomainName.toLowerCase();
    }
  },
  data() {
    return {
      showLoader: false,
      domainStore,
      activeStep: 1,
      dnsRecords: [],
      steps: [
        this.$t('frontend_domain_wizard_step1'),
        this.$t('frontend_domain_wizard_step2'),
        this.$t('frontend_domain_wizard_step3'),
        this.$t('frontend_domain_wizard_step4'),
        this.$t('frontend_domain_wizard_step5'),
      ],
      selectedPanel: '',
      setUpDomain: false,
      setUpCustomDomain: false,
      showCname: false,
      setupGrooveDomain: false,
      errorMsg: '',
      newSubDomain: '',
      showCreateDomain: false,
      newDomainName: '',
      newDomainType: 1,
      newDomain: null,
      addingDomain: false,
      verifyingDomain: false,
      domainHostingTypes: [
        {
          id: 1,
          title: this.$t('frontend_domain_wizard_domain_hosting_types1_title'),
          description: this.$t('frontend_domain_wizard_domain_hosting_types1_desc'),
        },
        {
          id: 2,
          title: this.$t('frontend_domain_wizard_domain_hosting_types2_title'),
          description: this.$t('frontend_domain_wizard_domain_hosting_types2_desc'),
        },
      ],
    };
  },
  created() {},
  methods: {
    showError(e) {
      console.error(e);
      if (e) {
        if (e && e.response && e.response.data.errors) {
          return Notify.fail(
            e.response.data.errors[0].message,
            e.response.data.errors.map(({ message }) => message).join('. ')
          );
        }
        return Notify.fail(e.message);
      }
    },
    validURL(str) {
      const pattern = new RegExp(
        '^(https?:\\/\\/)?' + // protocol
          '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
          '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
          '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
          '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
          '(\\#[-a-z\\d_]*)?$',
        'i'
      ); // fragment locator
      return !!pattern.test(str);
    },
    saveFirstStep() {
      this.activeStep = 2;
    },
    async saveSecondStep() {
      if (
        this.newDomainName.trim() != '' &&
        isValidDomain(this.newDomainName, { subdomain: false })
      ) {
        this.showLoader = true;

        try {
          const { status, message } = await this.$store.dispatch(
            'domains/addDomain',
            {
              name: this.newDomainName,
              type: this.newDomainType,
            }
          );

          if (status == 'success') {
            Notify.success(this.$t('frontend_domain_wizard_added_domain_successfully'), 'bottom-right');
            this.addingDomain = false;
            this.activeStep = 3;
          } else if (status == 'duplicate') {
            this.errorMsg = this.$t('frontend_domain_wizard_domain_name_is_unavailable');
            this.addingDomain = false;
            this.activeStep = 2;
          } else if (status == 'failed') {
            this.errorMsg = message.message;
            this.addingDomain = false;
            this.activeStep = 2;
          }
        } catch (e) {
          this.showError(e);
        } finally {
          this.showLoader = false;
        }
      } else {
        this.activeStep = 2;
        this.errorMsg = this.$t('frontend_domain_wizard_enter_valid_domain_name_error');
      }
    },
    async validateDomainRecords() {
      this.showLoader = true;
      try {
        const domainStatus = await this.$store.dispatch(
          'domains/verifyDNSRecord',
          { id: this.selectedDomain.id }
        );

        

        if (domainStatus == 'success') {
          Notify.success(this.$t('frontend_domain_wizard_successfully_verified_name'), 'bottom-right');
          this.activeStep = 5;
        } else if (domainStatus == 'pending') {
          Notify.warn(this.$t('frontend_domain_wizard_domain_status_check_initiated'), 'bottom-right');
          this.activeStep = 5;
        } else if (domainStatus == 'invalid') {
          Notify.fail(this.$t('frontend_domain_wizard_unable_to_very_domain'), 'bottom-right');
        } else {
          Notify.fail(this.$t('frontend_domain_wizard_domain_deleted_or_moved'), 'bottom-right');
        }
      } catch (e) {
        Notify.fail(e, 'bottom-right');
      } finally {
        this.showLoader = false;
      }
    },
    async skipVerification() {
      this.activeStep = 5;
    },
  },
};
</script>
